import type { TemplateSettingsSchemaT } from "./zod/quickstart-settings.ts";

const SKIP_SURGERY = "skip";

/**
 * Short label shown to patients on the clinician public templates page.
 * Kept in sync with `services/supabase/functions/_shared/template-patient-label.ts`.
 *
 * Does not use the templates table `template_name` column; only quickstart `settings.name` when set.
 *
 * Priority: injection substance phrase → surgery option name → education name (no extra wording) →
 * `settings.name` → `"Follow-up"`.
 */
export function deriveTemplatePatientLabel(
    settings: Partial<TemplateSettingsSchemaT>,
    surgeryNameById: Map<string, string>
): string {
    const events = settings.required_events ?? [];

    if (events.includes("injection")) {
        const details = settings.injection_details ?? [];
        for (const d of details) {
            const sub = d.injection_substance?.trim();
            if (sub) {
                const noun = sub.charAt(0).toUpperCase() + sub.slice(1);
                return `${noun} injection`;
            }
        }
    }

    const sid = settings.surgery_option_id?.trim();
    if (events.includes("surgery") && sid && sid !== SKIP_SURGERY) {
        const name = surgeryNameById.get(sid)?.trim();
        if (name) {
            return name;
        }
        return "Surgery";
    }

    if (events.includes("education")) {
        const en = settings.education_name?.trim();
        if (en) {
            return en;
        }
    }

    const fallback = settings.name?.trim();
    if (fallback) {
        return fallback;
    }
    return "Follow-up";
}
